import {
  deleteImageHelper,
  findChecklistValueForVisualField,
  uploadImageHelper,
  prepareChecklistMediaUpload,
} from '../../utils/file/uploadImageHelper';
import { v4 as uuidv4 } from 'uuid';
import { beginMediaAttempt, retainMediaPreparation, claimMediaPreparations } from '@/services/diagnostics/mediaAttemptRuntime';
import { mediaErrorCode } from '@/services/diagnostics/MediaDiagnosticHistory';
import { globalState } from '@/state/globalState';
import { databaseService } from '../databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { ChecklistImageServiceLogger } from '../../utils/logger';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';

const isLocalChecklistValue = (value: string): boolean => (
  value.startsWith('file://') ||
  value.startsWith('blob:') ||
  value.startsWith('checklist-')
);

const isRemoteChecklistValue = (value: string): boolean => (
  value.startsWith('http://') || value.startsWith('https://')
);

const findPreviousChecklistValue = async (
  checklistId: string,
  campoId: string
): Promise<{ valor?: string | null } | undefined> => {
  const checklist = await databaseService.readItem<{
    empresa_id?: string | null;
    snapshot_id?: string | null;
  }>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, checklistId);

  if (checklist?.empresa_id) {
    return findChecklistValueForVisualField(
      checklist.empresa_id,
      checklistId,
      campoId,
      checklist.snapshot_id
    );
  }

  // O fallback mantém compatibilidade com dados legados sem contexto de
  // empresa/snapshot; não inventa uma identidade de snapshot neste caso.
  const values = await databaseService.listItems<any>(
    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
    { checklist_id: checklistId }
  );
  return values.find((value: any) => (
    !value.snapshot_field_id && value.campo_id === campoId
  ));
};

export interface ChecklistMediaResult {
  recoveryPending?: boolean;
  retry?: () => Promise<ChecklistMediaResult>;
  success: boolean;
  remoteAcknowledged?: boolean;
  data?: { uri: string };
  error?: string;
  superseded?: boolean;
}

type ResumedMedia = { checklistId: string; fieldId: string; uri: string; localUri: string; expectedValue: string };
const resumedListeners = new Set<(event: ResumedMedia) => void>();
export const subscribeResumedChecklistMedia = (listener: (event: ResumedMedia) => void): (() => void) => {
  resumedListeners.add(listener);
  return () => { resumedListeners.delete(listener); };
};

const uploadChecklistMedia = async (
  checklistId: string,
  campoId: string,
  localUri: string,
  onLocalPersisted?: (uri: string) => void,
  attemptId = uuidv4(),
  expectedValue?: string
): Promise<ChecklistMediaResult> => {
  await beginMediaAttempt({ id: attemptId, checklistId, fieldId: campoId, source: 'upload' });
  const retained = expectedValue !== undefined && retainMediaPreparation(attemptId, { localUri, expectedValue });
  let prepared: Awaited<ReturnType<typeof prepareChecklistMediaUpload>> | undefined;
  try {
    prepared = await prepareChecklistMediaUpload(checklistId, campoId, localUri, attemptId, expectedValue);
    onLocalPersisted?.(localUri);
    const result = await uploadImageHelper({
      localUri, itemType: 'CHECKLIST', itemId: checklistId, campoId, pendingImageId: prepared.id,
    });
    if (result.superseded) return { success: true, superseded: true };
    return { success: true, data: { uri: result.publicURL }, remoteAcknowledged: result.remoteAcknowledged === true };
  } catch (error: any) {
    if (error?.message === 'media_preparation_superseded') return { success: true, superseded: true };
    if (!prepared) {
      captureSentryExceptionWithScope(error, {
        context: { flow: 'checklist_media', stage: 'local_prepare', checklistId, campoId },
        tags: { flow: 'upload', stage: 'local_prepare' },
      });
    }
    // Uma falha de rede não desfaz a preparação. Confirma o estado atual
    // antes de informar salvamento, sem restaurar a foto sobre outra edição.
    if (prepared && mediaErrorCode(error) !== 'source_missing') {
      try {
        const current = await findPreviousChecklistValue(checklistId, campoId);
        if (current?.valor !== localUri) return { success: true, superseded: true };
        const queued = await databaseService.readItem<any>('pending_images', prepared.id);
        if (queued?.local_uri === localUri) return { success: true, data: { uri: localUri }, remoteAcknowledged: false };
      } catch (verificationError) {
        captureSentryExceptionWithScope(verificationError, {
          context: { flow: 'checklist_media', stage: 'verify_local', checklistId, campoId, pendingId: prepared.id },
          tags: { flow: 'upload', stage: 'verify_local' },
        });
      }
    }
    return { success: false, error: error?.message || 'Não foi possível confirmar o salvamento da imagem.',
      ...(!prepared && retained ? { recoveryPending: true, retry: () => uploadChecklistMedia(checklistId, campoId, localUri, onLocalPersisted, attemptId, expectedValue) } : {}),
    };
  }
};

/** Retoma somente capturas próprias, preservadas antes da falha de preparação. */
export const resumeChecklistMediaPreparations = async (companyId: string, userId: string): Promise<void> => {
  const attempts = await claimMediaPreparations(companyId, userId);
  for (const attempt of attempts) {
    if (globalState.user.user.get()?.id !== userId || globalState.company.data.get()?.id !== companyId) return;
    const checklist = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, attempt.checklistId);
    if (checklist && checklist.empresa_id !== companyId) continue;
    const preparation = attempt.preparation!;
    const notify = (uri: string) => resumedListeners.forEach(listener => {
      try { listener({ checklistId: attempt.checklistId, fieldId: attempt.fieldId, uri, localUri: preparation.localUri, expectedValue: preparation.expectedValue }); } catch { /* Editor desmontado não desfaz a preparação. */ }
    });
    const result = await uploadChecklistMedia(attempt.checklistId, attempt.fieldId, preparation.localUri, notify, attempt.id, preparation.expectedValue);
    if (result.success && result.data?.uri) notify(result.data.uri);
  }
};

export const uploadChecklistPhoto = uploadChecklistMedia;
export const uploadChecklistSignature = uploadChecklistMedia;

export const deleteChecklistPhoto = async (
  checklistId: string,
  campoId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!checklistId || !campoId) {
      throw new Error('Parâmetros obrigatórios não informados');
    }

    // Buscar valor anterior do campo (draft ou valor salvo)
    let previousLocalUri: string | null = null;
    let previousImageUrl: string | null = null;
    const existente = await findPreviousChecklistValue(checklistId, campoId);
    if (existente) {
      const valorAnterior = existente.valor;
      if (valorAnterior) {
        if (isLocalChecklistValue(valorAnterior)) {
          previousLocalUri = valorAnterior;
        } else if (isRemoteChecklistValue(valorAnterior)) {
          previousImageUrl = valorAnterior;
        }
      }
    }

    // Remoção lógica offline: pai, empresa e tombstones locais são conferidos na transação.
    // O preflight remoto continua no PUSH; nenhum arquivo é apagado aqui.

    // Chama o helper de delete para CHECKLIST
    const result = await deleteImageHelper({
      itemType: 'CHECKLIST',
      itemId: checklistId,
      campoId,
      previousLocalUri,
      previousImageUrl,
    });

    return result;
  } catch (error: any) {
    return {
      success: false,
      error: error?.message || 'Erro ao remover foto do checklist',
    };
  }
};

export const deleteChecklistSignature = async (
  checklistId: string,
  campoId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!checklistId || !campoId) {
      throw new Error('Parâmetros obrigatórios não informados');
    }

    // Buscar valor anterior do campo (draft ou valor salvo)
    let previousLocalUri: string | null = null;
    let previousImageUrl: string | null = null;
    const existente = await findPreviousChecklistValue(checklistId, campoId);
    if (existente) {
      const valorAnterior = existente.valor;
      if (valorAnterior) {
        if (isLocalChecklistValue(valorAnterior)) {
          previousLocalUri = valorAnterior;
        } else if (isRemoteChecklistValue(valorAnterior)) {
          previousImageUrl = valorAnterior;
        }
      }
    }

    // Remoção lógica offline: pai, empresa e tombstones locais são conferidos na transação.
    // O preflight remoto continua no PUSH; nenhum arquivo é apagado aqui.

    // Chama o helper de delete para CHECKLIST
    const result = await deleteImageHelper({
      itemType: 'CHECKLIST',
      itemId: checklistId,
      campoId,
      previousLocalUri,
      previousImageUrl,
    });

    return result;
  } catch (error: any) {
    return {
      success: false,
      error: error?.message || 'Erro ao remover assinatura do checklist',
    };
  }
}; 
